import { Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControlLabel, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Tooltip, Typography } from '@mui/material'
import React, { HTMLInputTypeAttribute, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useDispatch } from 'react-redux'
import { setAddVetModal, setAddVetModalExam } from '../../store/reducers/modal.reducer'
import { t } from 'i18next'
import { z } from 'zod'
import { useMutation, useQueryClient } from 'react-query'
import { Exams } from '../../api/exams'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { isEmpty } from 'lodash'
import { Icon } from '@iconify/react'
import palette from '../../theme/palette'
import { useAuth } from '../../hooks/useAuth'

export type dtoShareWithVet = {
    "vet-email": string,
    "add-all-horse-trainings": boolean,
    "anonymize_horse": boolean,
}

function AddVetModal() {
    const open = useSelector<RootState>(state => state.modals.addVetModal) as boolean
    const examId = useSelector<RootState>(state => state.modals.addVetModalExam) as number
    const vets = useSelector<RootState>((state) => state.user.user?.vets) as { email: string }[]
    const dispatch = useDispatch()
    const [email, setEmail] = useState('')
    const [shareAll, setShareAll] = useState(false)
    const [isAnonyme, setIsAnonyme] = useState(false)
    const [error, hasError] = useState(false)
    const queryClient = useQueryClient()
    const { isAdmin } = useAuth()
    //Mutation
    const shareWithVetMutation = useMutation((data: dtoShareWithVet) => Exams.shareWithVet(examId, data))

    //Validation
    const emailSchema = z.string().email();

    const reset = () => {
        setEmail('')
        setShareAll(false)
        dispatch(setAddVetModal(false))
    }


    const share = async () => {
        try {
            await shareWithVetMutation.mutateAsync({ "vet-email": email, "add-all-horse-trainings": shareAll, 'anonymize_horse': isAnonyme })
            toast(t('rights:success'), { type: 'success' })
            queryClient.refetchQueries({ queryKey: ['auth'] })
            reset()
        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }
    }

    const validate = async () => {
        try {
            emailSchema.parse(email)
            hasError(false)
            await share()
        } catch (err) {
            hasError(true)
        }
    }

    const handleClose = () => {
        dispatch(setAddVetModal(false))
        hasError(false)
        setEmail('')
    }
    if (!open) return null

    return (
        <Dialog
            open={open}
            onClose={handleClose}
        >
            <DialogTitle>{t('shareVetModale:shareModalTitle')}</DialogTitle>

            <DialogContent>
                <Stack spacing={2}>


                    <DialogContentText>
                        <Typography variant='body2'>
                            {t('shareVetModale:shareModalIntro')}
                        </Typography>
                    </DialogContentText>
                    {
                        !isEmpty(vets) && <>
                            <Autocomplete
                                options={vets}
                                getOptionKey={option => option.email}
                                getOptionLabel={option => option.email}
                                renderInput={(params) => <TextField {...params} label={t('useExistingVetEmail')} />}
                                disableClearable
                                onChange={(_, value) => {
                                    setEmail(value?.email ?? "")
                                }}

                                disablePortal
                            />

                            <Divider></Divider>
                        </>
                    }


                    <TextField
                        helperText={error && t('form:error:email')}
                        error={error}
                        value={email}
                        onChange={e => {
                            setEmail(e.target.value)
                            hasError(false)
                        }}
                        type='email'
                        label={t('shareVetModale:emailLabel')}
                        fullWidth></TextField>
                    <FormControlLabel control={<Checkbox sx={{ pl: 0 }} checked={shareAll} onChange={(_, checked) => setShareAll(checked)} />} label={t('shareVetModale:shareAllTrainings')} />

                    {
                        isAdmin && <FormControlLabel control={<Checkbox sx={{ pl: 0 }} checked={isAnonyme} onChange={(_, checked) => setIsAnonyme(checked)} />} label={
                            <Typography variant='body2' display={'flex'} alignItems={'center'} gap={1}>
                                {t('anonymizeHorseLabel')}
                                <Tooltip arrow placement='right' title={t('anonymizeHorseHint')}>
                                    <Icon icon={'eva:info-outline'} fontSize={18} color={palette.dark.info.main}></Icon>

                                </Tooltip>
                            </Typography>} />
                    }


                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>{t('button:cancel')}</Button>
                <LoadingButton loading={shareWithVetMutation.isLoading} type="submit" variant='contained' onClick={validate}>{t('shareVetModale:confirm')}</LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default AddVetModal